import * as React from 'react';

import { Alert, Divider, Link, Tooltip, Typography, styled } from '@mui/material';
import { nanoid } from 'nanoid';
import { Trans, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose, type Dispatch } from 'redux';
import { Field, change, formValueSelector, reduxForm, type InjectedFormProps } from 'redux-form';

import type Carrier from '~/types/Carrier';
import type ProviderAccount from '~/types/ProviderAccount';

import {
  carrierFormFieldsClearState,
  fetchCarrierFormFields,
  type FetchCarrierFormFieldsPayload,
} from '~/actions/carrier-form-fields.actions';
import { fetchServiceLevelsByAccount } from '~/actions/providers.actions';
import * as iconCaretDown from '~/assets/icons/arrow-sea600.svg';
import * as iconChecked from '~/assets/icons/checkboxes/ic_checked.svg';
import * as iconIndeterminate from '~/assets/icons/checkboxes/ic_indeterminate.svg';
import * as iconUnchecked from '~/assets/icons/checkboxes/ic_unchecked.svg';
import * as iconExclamation from '~/assets/icons/exclamation-circle.svg';
import * as alertIcon from '~/assets/icons/icon_alert_info_filled.svg';
import { PrimaryAlternativeButton } from '~/components/Button';
import { InfoCard } from '~/components/Card';
import { CheckboxField } from '~/components/Checkbox';
import { DateTimePickerField } from '~/components/DateTimePicker';
import { default as ComponentIcon } from '~/components/Icon';
import { InputField, MaskedInputField } from '~/components/Input';
import { RedAsterisk } from '~/components/Label/Label';
import { Column, Row } from '~/components/Layout/Grid';
import { DeepBlueLink, LearnMoreLink } from '~/components/Link/Link';
import { ErrorMessage } from '~/components/MessageBox/Message';
import Modal from '~/components/Modal';
import { RadioField } from '~/components/Radio';
import { SmartSelectField } from '~/components/SmartSelect';
import { GreenSpinner } from '~/components/Spinner';
import { Body } from '~/components/Typography';
import * as carriers from '~/constants/carriers';
import { countries, type CountryIso2 } from '~/constants/countries';
import { CARRIER_FORM } from '~/constants/form';
import { getHelpLink } from '~/constants/links';
import { getEditCarrierServiceLevelsError } from '~/selectors/carrier-accounts.selectors';
import {
  getCarrierFormFieldValidations,
  getCarrierFormFields,
  getCarrierFormFieldsErrorMessage,
  getIsFetchingCarrierFormFields,
} from '~/selectors/carrier-form-fields.selectors';
import { getServiceLevelsByAccountRequest } from '~/selectors/providers.selectors';
import { type ApplicationState } from '~/store';
import { colors, fontWeight } from '~/styles/constants';
import { type CarrierFormField } from '~/types/CarrierFormField';
import { openLinkInNewTab } from '~/utils/navigation';
import { type InjectedTranslateProps } from '~/utils/translate';

import {
  AUSPOST_MYPOST_OPTIONS,
  isCarrierFormMocked,
} from '../SaveCarrierAccountModal/mock_parameters';

const SectionTitle = styled('header')`
  font-size: 18px;
  color: ${colors.form.fontColor};
  padding-bottom: 10px;
  font-weight: ${fontWeight.medium};
`;

const SectionBorder = styled('div')`
  margin: 20px auto;
  border: 0.5px solid ${colors.gray50};
`;

const carrierFormSidePadding = '32px';
const carrierFormTopBottomPadding = '20px';

export const CarrierFormModalBody = styled(Modal.Body)`
  padding: ${carrierFormTopBottomPadding} ${carrierFormSidePadding} 0.625rem;
  line-height: 20px;
  font-weight: ${fontWeight.normal};
`;

const StyledErrorMessage = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`;

const StyledAlertIcon = styled('i')`
  color: ${colors.dangerRed};
  vertical-align: middle;
  font-size: 2em;
  margin-right: 15px;
`;

// Styles HTML a tags with same color as BlueLink
const AgreementLabel = styled('span')`
  a {
    color: ${colors.aqua};
  }
`;

const Centered = styled('div')`
  text-align: center;
  margin: 20px;
`;

const TestCheckbox = styled(CheckboxField)`
  margin-bottom: 4px;
`;

const FedExByoaTermsAndConditionsCheckbox = styled(CheckboxField)`
  color: ${colors.form.labelFontColor};
`;

const FedExTermsAndConditionsLink = styled(DeepBlueLink)`
  font-size: inherit;
`;

const TestLabel = styled('div')`
  font-weight: ${fontWeight.medium};
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TestModeInfo = styled('div')<any>`
  color: ${(props) =>
    props.disabled ? colors.form.disabledLabelFontColor : colors.form.labelFontColor};
`;

const TestHelpLink = styled(LearnMoreLink)`
  font-size: inherit;
`;

const Disclaimer = styled('div')`
  line-height: 24px;
  font-size: 14px;
  color: ${colors.gray700};
  padding-bottom: 10px;
`;

const Subtext = styled('div')`
  color: ${colors.gray400};
  font-size: 0.75rem;
`;

const ServiceLevelsSection = styled('section')`
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled('div')`
  color: ${colors.gray700};
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-bottom: 1.25rem;
`;

const ShowHideLabel = styled('div')`
  font-weight: ${fontWeight.medium};
  color: ${colors.gray400};
`;

const ServiceLevelCheckbox = styled(CheckboxField)`
  margin-bottom: 0.75rem;
  margin-left: 0.25rem;
`;

const SelectAllToggleShowHide = styled('div')`
  margin-bottom: 1rem;
  display: flex;
  gap: 0.3125rem;
  cursor: pointer;
`;

const Icon = styled('img')`
  margin: 0 0.19rem;
  vertical-align: middle;
`;

const ViewMoreBtn = styled(PrimaryAlternativeButton)`
  font-weight: ${fontWeight.normal};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  border: none;
  width: 100%;
`;

const ErrorHeader = styled('div')`
  font-size: 0.8125rem;
  font-weight: ${fontWeight.medium};
  line-height: 1.25rem;
  color: ${colors.black};
  margin-bottom: 0.5rem;
`;

const ErrorDetails = styled('div')`
  font-size: 0.75rem;
  font-weight: ${fontWeight.normal};
  line-height: 1.25rem;
  color: ${colors.black};
`;

const AlertPanel = styled(InfoCard)`
  background-color: ${colors.macaroni50};
  border-color: ${colors.macaroni700};
  border-radius: 3px;
  font-size: 0.875rem;
  margin-bottom: '0.5rem';
  padding: 1rem 1rem 1rem 0.75rem;

  @media only screen and (min-width: 1024px) {
    border-radius: 3px;
  }
`;

export enum CarrierFormType {
  Connect = 'connect',
  Create = 'create',
  Edit = 'edit',
}

export enum CarrierFormEventType {
  Click = 'Click',
  Close = 'Close',
  Submit = 'Submit',
}

export enum TabbedCarrierFormName {
  EbayCanadaPost = 'Ebay Canada Post',
  UpsMarketplace = 'UPS Marketplace',
}

interface CarrierFormProps {
  accountCountry?: CountryIso2;
  carrier: Carrier;
  carrierAccountWithServiceLevels?: ProviderAccount;
  disabled?: boolean;
  errorMessage?: string; // error message from form submission
  fedExInternationalSection?: React.ReactNode;
  forceMockedParameters?: boolean;
  formType: CarrierFormType;
  hideAccountNickname?: boolean;
  hideTestMode?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialValues: { [key: string]: any };
  isUpsOauthNewRegistration?: boolean;
  masterAccountForm?: boolean;
  parameters: CarrierFormField[];
}

interface State {
  mockedValidations: { [key: string]: ((data: string) => void)[] };
  selectedCarrier: ProviderAccount | null;
  serviceLevelsIsHidden: null | string;
  viewMoreStatus: boolean;
}

interface StateProps {
  carrierFormFields: readonly CarrierFormField[];
  fetchErrorMessage: string; // error message from fetching
  formState: ApplicationState; // form state is used to access values of fields in the form
  getServiceLevelsError: string[];
  isFetchingCarrierFormFields: boolean;
  serviceLevelsByAccount: Requestable<ProviderAccount[], string>;
  validations: { [key: string]: ((data: string) => void)[] };
}

interface DispatchProps {
  clearCarrierFormFieldsState: () => void;
  clearField: (key: string) => void;
  fetchCarrierFormFields: (payload: FetchCarrierFormFieldsPayload) => void;
  fetchServiceLevelsAccounts: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setField: (key: string, value: any) => void;
}

enum ServiceLevelsIsHidden {
  All = 'all',
  Mix = 'mix',
  None = 'none',
}

const MAX_COUNT = 10; // number of service levels checkbox options to display

type ComponentProps = CarrierFormProps &
  DispatchProps &
  InjectedFormProps &
  InjectedTranslateProps &
  StateProps;

class CarrierForm extends React.Component<ComponentProps, State> {
  onToggleSelectAll = () => {
    const { selectedCarrier, serviceLevelsIsHidden } = this.state;
    if (!selectedCarrier) return;

    // change value to boolean to set checkbox.
    // ServiceLevelsIsHidden.All and ServiceLevelsIsHidden.Mix state is === to false.
    const allCheckboxChecked = serviceLevelsIsHidden === ServiceLevelsIsHidden.None ? true : false;

    for (const serviceLevel of selectedCarrier.serviceLevels)
      if (!serviceLevel.is_used) this.props.change(serviceLevel.token, !allCheckboxChecked);

    const setCheckboxState = !allCheckboxChecked
      ? ServiceLevelsIsHidden.None
      : ServiceLevelsIsHidden.All;

    this.setState({
      serviceLevelsIsHidden: setCheckboxState, //track the state for Select All Service Levels toggle.
    });
  };

  // render Service Levels list view more state
  onToggleViewMore = () => {
    const { selectedCarrier, viewMoreStatus } = this.state;
    if (!selectedCarrier) return;
    const len = selectedCarrier.serviceLevels.length;
    if (len <= MAX_COUNT) return;
    const content = viewMoreStatus ? 'less' : 'more';
    const arrowPosition = { transform: `rotate(${viewMoreStatus ? '180' : '0'}deg)` };

    return (
      <ViewMoreBtn onClick={() => this.setState({ viewMoreStatus: !viewMoreStatus })}>
        {`View ${content}`}
        <Icon src={iconCaretDown} style={arrowPosition} />
      </ViewMoreBtn>
    );
  };

  renderApiOnlyMessage = () => (
    <AlertPanel>
      <div className="flex">
        <ComponentIcon alt="" className="mr-3" size="16px" src={alertIcon} />
        <div className="leading-6">
          <Trans
            components={[
              <Link
                color="secondary"
                href="https://goshippo.com/docs/reference#carrier-accounts"
                key={nanoid()}
              >
                _
              </Link>,
              <Link color="secondary" href={getHelpLink} key={nanoid()} target="_blank">
                _
              </Link>,
            ]}
            i18nKey="carriers.form.serviceLevelsSection.apiOnlyMsg"
            ns="settings"
          />
        </div>
      </div>
    </AlertPanel>
  );

  renderErrorMessage = () =>
    this.props.errorMessage !== '' && (
      <ErrorMessage>
        <StyledErrorMessage>
          <StyledAlertIcon className={`fa fa-exclamation-circle`} />
          <strong>{this.props.errorMessage}</strong>
        </StyledErrorMessage>
      </ErrorMessage>
    );

  renderFedExByoaTermsAndConditions = () => {
    const { t } = this.props;
    return (
      <React.Fragment>
        <Field
          component={FedExByoaTermsAndConditionsCheckbox}
          label={
            <span>
              {t('carriers.form.byoaTermsAndConditions')}{' '}
              <FedExTermsAndConditionsLink
                href="https://www.fedex.com/content/dam/fedex-com/hdn/FedEx_Standard_End_User_License_Agreement_EULA_for_Hosted_3rd_party_solutions.pdf"
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('carriers.form.byoaFedexTermsAndConditions')}
              </FedExTermsAndConditionsLink>
              {/* Manually put asterisk at end of text since labelProps of 'required' puts the asterisk at the end of the css */}
              <RedAsterisk />
            </span>
          }
          name="fedex_byoa_terms_and_conditions"
          required
          validate={this.validateFedExByoaTermsRequired}
        />
      </React.Fragment>
    );
  };

  renderFetchErrorMessage = () =>
    this.props.fetchErrorMessage !== '' && (
      <ErrorMessage>
        <StyledErrorMessage>
          <StyledAlertIcon className={`fa fa-exclamation-circle`} />
          <strong>{this.props.fetchErrorMessage}</strong>
        </StyledErrorMessage>
      </ErrorMessage>
    );

  renderSectionBorder = (shouldHideSectionBorder?: boolean) =>
    !shouldHideSectionBorder && <SectionBorder />;

  renderServiceLevelsErrorMessage = () => {
    const errors = this.props.getServiceLevelsError;

    if (errors.length === 0) return;

    return (
      <ErrorMessage>
        <StyledErrorMessage>
          <Icon src={iconExclamation} />
          <div>
            <ErrorHeader>Some settings could not be saved.</ErrorHeader>
            <ul>
              {errors.map((error) => (
                <li key={error}>
                  <ErrorDetails>{error}</ErrorDetails>
                </li>
              ))}
            </ul>
          </div>
        </StyledErrorMessage>
      </ErrorMessage>
    );
  };

  renderServiceLevelsList = () => {
    const { selectedCarrier, viewMoreStatus } = this.state;
    if (!selectedCarrier) return;

    let list = viewMoreStatus
      ? selectedCarrier.serviceLevels
      : selectedCarrier.serviceLevels.slice(0, MAX_COUNT);

    return list.map((serviceLevel) => {
      const inputProps = {
        disabled: serviceLevel.is_used,
        label: serviceLevel.name,
        labelProps: {},
        name: serviceLevel.token,
      };
      return this.serviceLevelCheckboxInput(inputProps);
    });
  };

  renderServiceLevelsSection = () => {
    const { t } = this.props;

    enum CheckboxIconsState {
      all = iconUnchecked,
      mix = iconIndeterminate,
      none = iconChecked,
    }

    return (
      <>
        <SectionBorder />
        <SectionTitle>{t('carriers.form.serviceLevelsSection.header')}</SectionTitle>
        <SectionSubtitle>
          <Trans
            components={[<strong key={0}>0</strong>]}
            i18nKey="carriers.form.serviceLevelsSection.subtitle"
            ns="settings"
          />
        </SectionSubtitle>
        {this.renderServiceLevelsErrorMessage()}
        <ServiceLevelsSection>
          <SelectAllToggleShowHide onClick={this.onToggleSelectAll}>
            <Icon
              src={
                CheckboxIconsState[
                  (this.state.serviceLevelsIsHidden as keyof typeof CheckboxIconsState) || 'none'
                ] as unknown as string
              }
            />
            <ShowHideLabel>{t('carriers.form.serviceLevelsSection.showHideAll')}</ShowHideLabel>
          </SelectAllToggleShowHide>
          {this.renderServiceLevelsList()}
          {this.onToggleViewMore()}
        </ServiceLevelsSection>
      </>
    );
  };

  renderTestLabel = (carrier: Carrier) => {
    const { disabled, t } = this.props;
    return (
      <React.Fragment>
        <Field
          component={TestCheckbox}
          disabled={disabled}
          label={
            <TestLabel>
              {carriers.mustBeActualTestAccounts.includes(carrier.id)
                ? t('carriers.form.enableActualTestAccount')
                : t('carriers.form.enableTestMode')}
            </TestLabel>
          }
          labelProps={{ disabled }}
          name="test"
          props={{ dataTestid: 'test-mode-checkbox' }}
          responsive
        />
        <TestModeInfo disabled={disabled}>
          {carriers.mustBeActualTestAccounts.includes(carrier.id) ? (
            t('carriers.form.actualTestAccount', { carrierName: carrier.name })
          ) : (
            <span>
              {t('carriers.form.testAccountInfo')}&nbsp;
              <TestHelpLink
                href="https://support.goshippo.com/hc/en-us/articles/360003902611"
                target="_blank"
              >
                {t('carriers.form.testLearnMore')}
              </TestHelpLink>
              .
            </span>
          )}
        </TestModeInfo>
      </React.Fragment>
    );
  };

  serviceLevelCheckboxInput = ({
    disabled,
    label,
    labelProps,
    name,
  }: {
    disabled?: boolean;
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    labelProps: any;
    name: string;
  }) => {
    if (!disabled) {
      return (
        <Field
          component={ServiceLevelCheckbox}
          key={name}
          label={label}
          labelProps={labelProps}
          name={name}
          responsive
        />
      );
    }

    return (
      <Tooltip
        key={name}
        placement="top-start"
        title={this.props.t('carriers.form.serviceLevelsSection.disabledTooltipMsg')}
      >
        <span>
          <Field
            component={ServiceLevelCheckbox}
            disabled
            label={label}
            labelProps={labelProps}
            name={name}
            responsive
          />
        </span>
      </Tooltip>
    );
  };

  // setServiceLevelState will only be set once
  setServiceLevelState = () => {
    if (!this.props.carrierAccountWithServiceLevels) return;

    const { carrierAccountWithServiceLevels, formType } = this.props;
    const isEditForm = formType === CarrierFormType.Edit;
    const isHiddenReadyToSet = this.state.serviceLevelsIsHidden === null;
    const hasServiceLevels = carrierAccountWithServiceLevels.hasOwnProperty('serviceLevels');

    if (!isEditForm || !(isHiddenReadyToSet && hasServiceLevels)) return;

    const serviceLevels = carrierAccountWithServiceLevels.serviceLevels;
    let carrierAccountServiceLevels: string[] = [];
    let hiddenCount = { isHidden: 0, notHidden: 0 };
    let toggleState = ServiceLevelsIsHidden.All;

    for (const serviceLevel of serviceLevels) {
      carrierAccountServiceLevels.push(serviceLevel.token);
      if (!serviceLevel.is_used)
        serviceLevel.is_hidden ? hiddenCount.isHidden++ : hiddenCount.notHidden++;
    }

    // set Select All Service Levels toggle state. if hiddenCount.isHidden and hiddenCount.notHidden is positive, set state to mix state.
    if (hiddenCount.isHidden && hiddenCount.notHidden) {
      toggleState = ServiceLevelsIsHidden.Mix;
    } else {
      toggleState =
        hiddenCount.isHidden >= hiddenCount.notHidden
          ? ServiceLevelsIsHidden.All
          : ServiceLevelsIsHidden.None;
    }

    this.setState({
      selectedCarrier: carrierAccountWithServiceLevels,
      serviceLevelsIsHidden: toggleState,
    });
  };

  // set the state of Carrier Service levels based on is_hidden flag. (all, none, mix)
  // set selected Carrier to be Edit
  setValidations = (parameters: CarrierFormField[]) => {
    const mockedValidations: { [key: string]: ((data: string) => void)[] } = {};

    const validateIsRequired = (value: string) => (!value ? 'Required' : undefined);

    const validateMaxLength = (max: number) => (value: string) =>
      value && value.length > max
        ? `Maximum length is ${max} characters (currently ${value.length})`
        : undefined;

    const validateMinLength = (min: number) => (value: string) =>
      value && value.length < min
        ? `Minimum length is ${min} characters (currently ${value.length})`
        : undefined;

    const validateIntegerType = (value: string) =>
      value && isNaN(Number(value)) ? 'Must be a number' : undefined;

    const validateRegex = (regex: RegExp, errorFormat: string) => (value: string) =>
      value && !regex.test(value) ? `Must be in format ${errorFormat}` : undefined;

    for (const parameter of parameters) {
      const parameterValidations: ((data: string) => void)[] = [];

      if (parameter.fieldType === 'integer') parameterValidations.push(validateIntegerType);

      if (parameter.isRequired) parameterValidations.push(validateIsRequired);

      if (parameter.maxLength) parameterValidations.push(validateMaxLength(parameter.maxLength));

      if (parameter.minLength) parameterValidations.push(validateMinLength(parameter.minLength));

      if (parameter.fieldType === 'military_time')
        parameterValidations.push(validateRegex(/^(2[0-3]|[0-1][\d]):[0-5][\d]$/, 'HH:MM'));

      mockedValidations[parameter.key] = parameterValidations;
    }

    this.setState({
      mockedValidations,
    });
  };

  validateFedExByoaTermsRequired = (value: boolean) =>
    !value ? `${this.props.t('carriers.form.required')}` : undefined;

  validateMetadataField = (value: string) => {
    const METADATA_MAX_LENGTH = 100;
    return value && value.length > METADATA_MAX_LENGTH
      ? `Maximum length is ${METADATA_MAX_LENGTH} characters (currently ${value.length})`
      : undefined;
  };

  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      mockedValidations: {},
      selectedCarrier: null,
      serviceLevelsIsHidden: null,
      viewMoreStatus: false,
    };
  }

  componentDidMount() {
    const { carrier, forceMockedParameters, formType } = this.props;

    // Don't need to fetch if carrier params are mocked
    if (!isCarrierFormMocked(carrier.id, formType) && !forceMockedParameters)
      this.props.fetchCarrierFormFields({ carrier, formType });
    else this.setValidations(this.props.parameters); // Set validations for mocked parameters

    // set initial serviceLevelsIsHidden state and selected Carrier Account
    this.setServiceLevelState();
  }

  componentDidUpdate(prevProps: CarrierFormProps) {
    const { parameters } = this.props;
    // If parameters changed (i.e. Create form changed to Connect form), set validations for the new fields
    if (prevProps.parameters !== parameters && parameters) this.setValidations(parameters);

    // set initial serviceLevelsIsHidden state and selected Carrier Account
    this.setServiceLevelState();
  }

  componentWillUnmount() {
    this.props.clearCarrierFormFieldsState();
  }

  handleLinkOnClick(e: React.MouseEvent<HTMLSpanElement>) {
    // If the anchor is clicked, stop checkbox toggle (propagation) and stop default redirect - use openLinkInNewTab instead

    // TODO: The compiler does not like these but it would like them if they were e.currentTarget
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((e.target as any).tagName.toLowerCase() === 'a') {
      e.stopPropagation();
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      openLinkInNewTab((e.target as any).href);
    }
  }

  render() {
    const {
      accountCountry,
      carrier,
      disabled,
      fetchErrorMessage,
      forceMockedParameters,
      formState,
      formType,
      hideAccountNickname,
      hideTestMode,
      isFetchingCarrierFormFields,
      masterAccountForm,
      validations,
    } = this.props;

    const { mockedValidations } = this.state;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let carrierFormFields: any;

    // Use mocked data for now; set from this.props once we start using fetched data
    if (isCarrierFormMocked(carrier.id, formType) || forceMockedParameters)
      carrierFormFields = this.props.parameters;
    else carrierFormFields = this.props.carrierFormFields;

    let defaultErrorMessage = this.renderFetchErrorMessage();
    let showErrorMessage = fetchErrorMessage !== '' && !masterAccountForm;
    if (carrier.id === 'lasership') {
      // Remove this patch after migrating lasership see https://shippo.atlassian.net/browse/ENG-32075
      defaultErrorMessage = this.renderApiOnlyMessage();
      showErrorMessage = true;
    }

    return isFetchingCarrierFormFields && !forceMockedParameters ? (
      <Centered>
        <GreenSpinner />
      </Centered>
    ) : (
      <form>
        <CarrierFormModalBody>
          {
            // If there's a fetch error message, only render that; otherwise render the fields
            showErrorMessage ? (
              defaultErrorMessage
            ) : (
              <React.Fragment>
                {!masterAccountForm && (
                  <React.Fragment>
                    {this.renderErrorMessage()}
                    {!hideAccountNickname && (
                      <Row>
                        <Column width="100%">
                          <Field
                            component={InputField}
                            disabled={disabled}
                            label="Account Nickname"
                            labelProps={{ disabled }}
                            name="metadata"
                            placeholder="e.g. Test Account, US Domestic, CA Facility"
                            responsive
                            validate={this.validateMetadataField}
                          />
                        </Column>
                      </Row>
                    )}
                    {carrierFormFields.map((carrierFormField: CarrierFormField, index: number) => {
                      let component;

                      // this is a hack to by-pass validation if a masked input field is pristine.
                      // reason for this hack is because FE/BE requires a min length, BE provides an initial masked value of 6 asterisks "******". on submit of any form change, FE validation will fail.
                      const handlePasswordRequiredState = () => {
                        const initialValue = this.props.initialValues?.[carrierFormField.key];
                        const currentValue = selector(formState, carrierFormField.key);
                        return (
                          initialValue === currentValue &&
                          formType === CarrierFormType.Edit &&
                          carrierFormField.displayType === 'mask'
                        );
                      };

                      // if we are hiding the account nickname field and this form field will be at the top, this boolean will be used to prevent
                      // the rendering of an ugly-looking empty section where 'Account Nickname' would have been
                      const shouldHideSectionBorder = index === 0 && hideAccountNickname;

                      const isAusPostMyPostRadioField =
                        carrier.id === carriers.AUSTRALIA_POST.id &&
                        formType === CarrierFormType.Connect &&
                        carrierFormField.key === 'is_mypost';

                      if (
                        !CONFIG.flags.enableAusPostMyPost &&
                        (isAusPostMyPostRadioField ||
                          (carrier.id === carriers.AUSTRALIA_POST.id &&
                            carrierFormField.key === 'merchant_token'))
                      )
                        return null;

                      if (isAusPostMyPostRadioField) {
                        // Radio buttons aren't supported by the DB yet but we need to support radio buttons for AusPost MyPost.
                        // AusPost is already backed by the DB (not mocked) so we have to dynamically attach these options to
                        // the formfield we fetched from the DB.
                        carrierFormField.options = AUSPOST_MYPOST_OPTIONS;
                      }

                      // Hack to prepopulate AusPost MyPost radio button since radio button defaults aren't yet supported by DB
                      if (isAusPostMyPostRadioField && !selector(formState, carrierFormField.key))
                        this.props.setField('is_mypost', AUSPOST_MYPOST_OPTIONS[0]);

                      // Flag used as a hardcoded check for UPS Create form country dropdown field
                      // The field should only have one option (the country of the master account) and be disabled since there are no other options
                      const isUPSCreateCountryField =
                        carrier.id === 'ups' &&
                        formType === CarrierFormType.Create &&
                        carrierFormField.key.endsWith('country_iso2');

                      // Hack for UPS Canada master since terms agreements links stored in the db are specific to UPS US master
                      // Hardcoding the Canada links here
                      if (
                        carrier.id === 'ups' &&
                        formType === CarrierFormType.Create &&
                        carrierFormField.key === 'ups_agreements' &&
                        accountCountry === countries.CA.iso2
                      ) {
                        carrierFormField.label =
                          'By checking this box, you agree to the <a rel="noreferrer noopener" target="_blank" href="https://assets.ups.com/adobe/assets/urn:aaid:aem:c271a615-2207-4626-8507-521abe266d37/original/as/uta-en-GLOBAL.pdf">UPS Technology Agreement</a> and <a href="https://shippo.com/CA-TToCos">UPS Tariff/Terms and Conditions of Service</a> and <a href="https://shippo.com/CA-LoPI">List of Prohibited Items for Shipping</a>';
                      }

                      // Check if parameter could be hidden by another parameter (hiddenByKey)
                      if (carrierFormField.hiddenByKey) {
                        const valuePresent = selector(formState, carrierFormField.hiddenByKey);

                        // Hacks for mypost to support hiding based on which radio button is selected.
                        // At the time of writing, hiding by string option is not supported in the db
                        let shouldHideAusPostFields = false;
                        if (carrierFormField.hiddenByKey === 'is_mypost') {
                          if (carrierFormField.hiddenByValue && valuePresent?.value === 'is_mypost')
                            shouldHideAusPostFields = true;
                          else if (
                            !carrierFormField.hiddenByValue &&
                            valuePresent?.value === 'is_eparcel'
                          )
                            shouldHideAusPostFields = true;
                        }

                        // If the hiddenByKey parameter contains value then clear the current field and do not render it
                        if (
                          valuePresent === carrierFormField.hiddenByValue ||
                          shouldHideAusPostFields
                        ) {
                          // Clear the field to be hidden if it has a value
                          if (selector(formState, carrierFormField.key))
                            this.props.clearField(carrierFormField.key);

                          return null;
                        }

                        // Hack to pre-populate the Pickup Address Country field when it appears (since it's disabled)
                        if (
                          carrierFormField.key === 'pickup_address_country_iso2' &&
                          !selector(formState, carrierFormField.key)
                        ) {
                          let countryOption;
                          if (accountCountry && carrierFormField.options) {
                            countryOption = carrierFormField.options.filter(
                              (o) => o.value === accountCountry,
                            )[0]; // There should only be one match
                          }
                          // Not using an else clause so the default also gets set in case the country is not present in carrierFormField.options
                          if (!countryOption) {
                            countryOption = {
                              label: countries.US.name,
                              value: countries.US.iso2,
                            };
                          }
                          this.props.setField('pickup_address_country_iso2', countryOption);
                        }
                      }

                      // Extract the label string from parameter. May convert to HTML element if boolean field type (for agreement fields)
                      let label: JSX.Element | string = carrierFormField.label;

                      switch (carrierFormField.fieldType) {
                        case 'integer':
                        case 'string':
                        case 'military_time':
                          if (
                            formType === CarrierFormType.Edit &&
                            carrierFormField.key !== 'account_id' &&
                            (carrierFormField.displayType === 'mask' ||
                              carrierFormField.displayType === 'show_partial')
                          )
                            component = MaskedInputField;
                          else component = InputField;

                          break;
                        case 'boolean':
                          if (isAusPostMyPostRadioField) {
                            component = RadioField;
                            break;
                          }
                          component = CheckboxField;
                          label = (
                            <AgreementLabel
                              dangerouslySetInnerHTML={{ __html: label }}
                              onClick={this.handleLinkOnClick}
                            />
                          );
                          break;
                        case 'choice':
                          component = SmartSelectField;
                          break;
                        case 'date':
                          component = DateTimePickerField;
                          break;
                        default:
                          // Return null if field type is not supported
                          return null;
                      }

                      const formField = (
                        <Field
                          component={component}
                          disabled={
                            (formType === CarrierFormType.Edit &&
                              carrierFormField.key === 'account_id') ||
                            disabled ||
                            carrierFormField.disabled ||
                            isUPSCreateCountryField // Hack to disable country dropdown for UPS Create Form (country of master account is only option)
                          }
                          id={carrierFormField.key}
                          label={label}
                          // TODO: checking the key for substring of 'password' is a hack. Ideally we would have a field type or display type for this
                          labelProps={{
                            disabled: disabled || carrierFormField.disabled,
                            required:
                              (formType === CarrierFormType.Edit &&
                                carrierFormField.key === 'account_id') ||
                              carrierFormField.disabled
                                ? false
                                : carrierFormField.isRequired,
                            tooltipText: carrierFormField.tooltip,
                          }}
                          linkText="Where is this?"
                          linkUrl={carrierFormField.helpUrl}
                          name={carrierFormField.key}
                          onClick={
                            component === MaskedInputField
                              ? () => this.props.clearField(carrierFormField.key)
                              : undefined
                          }
                          options={
                            isUPSCreateCountryField && carrierFormField.options
                              ? // Hack to only show country of master account for UPS Create Form
                                carrierFormField.options.filter(
                                  (o) =>
                                    o.value ===
                                    (accountCountry ? accountCountry : countries.US.iso2),
                                )
                              : carrierFormField.options
                          }
                          placeholder={carrierFormField.placeholderText!}
                          responsive
                          // (cannot use mask display type because account_id is sometimes masked but is not a password type)
                          type={
                            carrierFormField.key.includes('password') &&
                            !carrierFormField.key.includes('api_password')
                              ? 'password'
                              : undefined
                          }
                          validate={
                            handlePasswordRequiredState() ||
                            (formType === CarrierFormType.Edit &&
                              carrierFormField.key === 'account_id') ||
                            carrierFormField.disabled
                              ? undefined
                              : isCarrierFormMocked(carrier.id, formType) || forceMockedParameters
                              ? mockedValidations[carrierFormField.key]
                              : validations[carrierFormField.key]
                          }
                        />
                      );

                      // Hardcoding Section Labels for now (until form grouping of carrier params is supported in the db)
                      return (
                        <React.Fragment key={carrierFormField.key}>
                          <Row className={isAusPostMyPostRadioField ? 'mb-4' : ''}>
                            <Column width="100%">
                              {carrierFormField.key.endsWith('full_name') && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  {formType === CarrierFormType.Connect && ( // No Section Label for full_name if Create form
                                    <SectionTitle>Contact Information</SectionTitle>
                                  )}
                                </React.Fragment>
                              )}
                              {carrierFormField.key.endsWith('first_name') && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  <SectionTitle>Contact Information</SectionTitle>
                                </React.Fragment>
                              )}
                              {carrier === carriers.UPS &&
                                formType === CarrierFormType.Create &&
                                carrierFormFields[index - 1]?.key?.endsWith('company') && ( // Info text beneath UPS Master company field
                                  <Subtext>
                                    {this.props.t('carriers.upsMasterForm.companySubtext')}
                                  </Subtext>
                                )}
                              {(carrierFormField.key.endsWith('billing_address_st') ||
                                carrierFormField.key.endsWith('billing_address_st1') ||
                                carrierFormField.key.endsWith('billing_address_street') ||
                                carrierFormField.key.endsWith('billing_address_street1') ||
                                carrierFormField.key.endsWith('street_number')) && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  <SectionTitle>Billing Address</SectionTitle>
                                  {carrier === carriers.UPS &&
                                    formType === CarrierFormType.Create && (
                                      <Alert severity="info" sx={{ my: 1 }}>
                                        {this.props.t('carriers.upsMasterForm.addressAlert')}
                                      </Alert>
                                    )}
                                </React.Fragment>
                              )}
                              {(carrierFormField.key.endsWith('from_address_st') ||
                                carrierFormField.key.endsWith('from_address_st1') ||
                                carrierFormField.key.endsWith('from_address_street') ||
                                carrierFormField.key.endsWith('from_address_street1')) && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  <SectionTitle>Shipping Address</SectionTitle>
                                </React.Fragment>
                              )}
                              {carrierFormField.key.endsWith('same_as_billing_address') && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  <SectionTitle>Pickup Address</SectionTitle>
                                  <Disclaimer>Note: Pickup address cannot be a PO Box</Disclaimer>
                                </React.Fragment>
                              )}
                              {carrierFormField.key.includes('agreement') &&
                                this.renderSectionBorder(shouldHideSectionBorder)}
                              {carrierFormField.key.endsWith('account') && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  <SectionTitle>Account Information</SectionTitle>
                                </React.Fragment>
                              )}
                              {carrierFormField.key.endsWith('has_invoice') && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  <SectionTitle>Personalized UPS Rates</SectionTitle>
                                </React.Fragment>
                              )}
                              {carrierFormField.key.endsWith('legal_name') && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                  <SectionTitle>Business Information</SectionTitle>
                                </React.Fragment>
                              )}
                              {carrierFormField.key.endsWith('customer_number') && (
                                <React.Fragment>
                                  {this.renderSectionBorder(shouldHideSectionBorder)}
                                </React.Fragment>
                              )}
                              {carrierFormField.key.endsWith('is_mypost') &&
                                carrier.id === carriers.AUSTRALIA_POST.id && (
                                  <React.Fragment>
                                    {this.renderSectionBorder(shouldHideSectionBorder)}
                                  </React.Fragment>
                                )}
                              {carrierFormField.key.endsWith('merchant_token') &&
                                carrier.id === carriers.AUSTRALIA_POST.id && (
                                  <Body className="mb-2">
                                    {`${this.props.t('carriers.createAtCarrierWebsite', {
                                      accountType: 'MyPost',
                                      carrierName: carrier.name,
                                    })} `}
                                    <TestHelpLink
                                      href={'https://auspost.com.au/mypost-business'}
                                      target="_blank"
                                    >
                                      {this.props.t('carriers.clickToGetStarted')}
                                    </TestHelpLink>
                                  </Body>
                                )}
                              {formField}
                            </Column>
                          </Row>
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                )}
                {this.props.fedExInternationalSection && (
                  <React.Fragment>{this.props.fedExInternationalSection}</React.Fragment>
                )}
                {formType === CarrierFormType.Connect &&
                  carrier.id === 'fedex' &&
                  this.renderFedExByoaTermsAndConditions()}
                {
                  // Remove test mode option for Create forms and UPS connect forms
                  !(
                    (formType === CarrierFormType.Connect && carrier.id === 'ups') ||
                    (formType === CarrierFormType.Create &&
                      carriers.createFormCarriers.includes(carrier.id)) ||
                    hideTestMode
                  ) && (
                    <React.Fragment>
                      {
                        // Hide Section Border if master account form
                        this.renderSectionBorder(masterAccountForm)
                      }
                      <Row>
                        <Column width="100%">{this.renderTestLabel(carrier)}</Column>
                      </Row>
                    </React.Fragment>
                  )
                }
                {formType === CarrierFormType.Connect && carrier.id === 'fedex' && (
                  <>
                    <Divider />
                    <Typography sx={{ my: 2 }} variant="caption">
                      {this.props.t('carriers.form.newFedExFooterCopy')}
                    </Typography>
                    <Divider />
                  </>
                )}

                {formType === CarrierFormType.Connect && this.props.isUpsOauthNewRegistration && (
                  <Typography color={colors.gray700} style={{ textAlign: 'center' }}>
                    {this.props.t('carriers.upsOauthRegistrationForm.description')}
                  </Typography>
                )}

                {formType === CarrierFormType.Edit && this.renderServiceLevelsSection()}
                {this.props.children}
              </React.Fragment>
            )
          }
        </CarrierFormModalBody>
      </form>
    );
  }
}

const selector = formValueSelector(CARRIER_FORM);

const mapStateToProps = (state: ApplicationState): StateProps => ({
  carrierFormFields: getCarrierFormFields(state),
  fetchErrorMessage: getCarrierFormFieldsErrorMessage(state),
  formState: state,
  getServiceLevelsError: getEditCarrierServiceLevelsError(state),
  isFetchingCarrierFormFields: getIsFetchingCarrierFormFields(state),
  serviceLevelsByAccount: getServiceLevelsByAccountRequest(state),
  validations: getCarrierFormFieldValidations(state),
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  clearCarrierFormFieldsState: () => dispatch(carrierFormFieldsClearState()),
  clearField: (key: string) => dispatch(change(CARRIER_FORM, key, '')),
  fetchCarrierFormFields: (payload: FetchCarrierFormFieldsPayload) =>
    dispatch(fetchCarrierFormFields(payload)),
  fetchServiceLevelsAccounts: () => dispatch(fetchServiceLevelsByAccount()),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setField: (key: string, value: any) => dispatch(change(CARRIER_FORM, key, value)),
});

const ComposedCarrierForm =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compose<any>(
    withTranslation('settings'),
    reduxForm({
      enableReinitialize: true, // Allows prepoulate / prefill logic to update fields after fetch
      form: CARRIER_FORM,
    }),
    connect(mapStateToProps, mapDispatchToProps),
  )(CarrierForm);

export default ComposedCarrierForm;
